import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import api from '../../services/api';
import type { SupaSQBuilding, SupaSQUnit } from '../../types/supa';
import { supaBuildingKey } from '../../types/supa';


const UNIT_STATUS: Record<string, { label: string; color: 'success' | 'primary' | 'warning' | 'default' }> = {
  '300': { label: 'Available',            color: 'success' },
  '301': { label: 'Connected with Sonic', color: 'primary' },
  '302': { label: 'Transfer Available',   color: 'warning' },
};

function buildingLabel(b: SupaSQBuilding): string {
  return [
    b.buildingName,
    b.address_street_number && b.address_route
      ? `${b.address_street_number} ${b.address_route}`
      : b.address_route,
    b.address_locality,
    b.address_administrative_area_level_1,
    b.address_postal_code,
  ].filter(Boolean).join(', ');
}

function unitLabel(u: SupaSQUnit): string {
  return u.unitNo ? `Unit ${u.unitNo}` : u.addressLine1 || u.rid;
}

function formatUnitAddress(u: SupaSQUnit): string {
  return [
    u.unitNo ? `Unit ${u.unitNo}` : null,
    u.addressLine1,
    u.city,
    u.state,
    u.postcode,
  ].filter(Boolean).join(', ');
}

function formatBuildingAddress(b: SupaSQBuilding): string {
  return [
    b.buildingName,
    b.address_street_number,
    b.address_route,
    b.address_locality,
    b.address_administrative_area_level_1,
    b.address_postal_code,
  ].filter(Boolean).join(', ');
}

type Props = {
  value: string;
  onChange: (addr: string) => void;
  /** supaBuildingKey() of the building to auto-select on load (e.g. carried over from the address-check widget) — skips straight to unit selection. */
  initialBuildingKey?: string;
};

type SQState =
  | { phase: 'idle' }
  | { phase: 'loading' }
  | { phase: 'units'; units: SupaSQUnit[] }
  | { phase: 'building_only' }   // serviceable but no unit list returned
  | { phase: 'not_serviceable'; description: string }
  | { phase: 'error'; message: string };

export default function SupaSQAddressField({ value, onChange, initialBuildingKey }: Props) {
  const [buildings, setBuildings] = useState<SupaSQBuilding[]>([]);
  const [buildingsLoading, setBuildingsLoading] = useState(true);
  const [buildingsError, setBuildingsError] = useState<string | null>(null);

  const [selectedBuilding, setSelectedBuilding] = useState<SupaSQBuilding | null>(null);
  const [sq, setSq] = useState<SQState>({ phase: 'idle' });
  const [manualMode, setManualMode] = useState(false);
  const [autocompleteKey, setAutocompleteKey] = useState(0);

  useEffect(() => {
    api.publicSupaServicedBuildings()
      .then((data) => {
        setBuildings(data.servicedBuildings ?? []);
        setBuildingsLoading(false);
      })
      .catch(() => {
        setBuildingsError('Could not load address list');
        setBuildingsLoading(false);
      });
  }, []);

  // Auto-run SQ when building is selected
  const handleBuildingSelect = async (b: SupaSQBuilding) => {
    setSelectedBuilding(b);
    onChange('');
    setSq({ phase: 'loading' });
    try {
      const data = await api.publicSupaServiceQualification({
        StreetName: b.address_route,
        StreetNo: b.address_street_number || undefined,
        Suburb: b.address_locality,
        State: b.address_administrative_area_level_1 || undefined,
        Postcode: b.address_postal_code || undefined,
      });
      // The SQ API is queried by street address, and multiple buildings in the same
      // complex can share one street address — it returns units for the whole complex,
      // so filter down to the specific building the customer picked.
      const units = (data.buildingUnits ?? []).filter((u) => u.buildingName === b.buildingName);
      if (data.statusCode === '200' && units.length > 0) {
        setSq({ phase: 'units', units });
      } else if (data.statusCode === '200') {
        setSq({ phase: 'building_only' });
        onChange(formatBuildingAddress(b));
      } else if (data.statusCode === '402') {
        // Nearby — treat units if present, else not serviceable
        if (units.length > 0) {
          setSq({ phase: 'units', units });
        } else {
          setSq({ phase: 'not_serviceable', description: data.description || 'Address is not on the Supa Networks network.' });
        }
      } else {
        setSq({ phase: 'not_serviceable', description: data.description || 'Address is not on the Supa Networks network.' });
      }
    } catch (e: any) {
      setSq({ phase: 'error', message: e?.response?.data?.detail || e?.message || 'Service qualification check failed' });
    }
  };

  // Auto-select the building carried over via ?building= deep link, once the list has loaded
  useEffect(() => {
    if (!initialBuildingKey || buildingsLoading || selectedBuilding) return;
    const match = buildings.find((b) => supaBuildingKey(b) === initialBuildingKey);
    if (match) handleBuildingSelect(match);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialBuildingKey, buildingsLoading, buildings]);

  const handleReset = () => {
    setSelectedBuilding(null);
    setSq({ phase: 'idle' });
    onChange('');
    setAutocompleteKey((k) => k + 1);
  };

  const handleSelectUnit = (u: SupaSQUnit) => {
    onChange(formatUnitAddress(u));
  };

  // ── Manual fallback ──────────────────────────────────────────────────────────
  if (manualMode) {
    return (
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2">Service address</Typography>
          <Button size="small" onClick={() => setManualMode(false)} startIcon={<SearchIcon sx={{ fontSize: 14 }} />} sx={{ fontSize: '0.75rem' }}>
            Use address search
          </Button>
        </Box>
        <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
          Enter the full address including unit, suburb and state, e.g.{' '}
          <strong>Unit 3, 45 Marine Parade, Ettalong Beach NSW 2257</strong>
        </Alert>
        <TextField
          fullWidth multiline minRows={2} size="small" required
          label="Service address"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Unit/apt number, street, suburb, state, postcode"
        />
      </Box>
    );
  }

  // ── Confirmed address banner ─────────────────────────────────────────────────
  if (value && sq.phase !== 'units') {
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Service address</Typography>
        <Alert
          severity="success"
          icon={<CheckCircleIcon fontSize="small" />}
          sx={{ py: 0.5 }}
          action={<Button size="small" color="inherit" onClick={handleReset}>Change</Button>}
        >
          <Typography variant="body2" fontWeight={600}>{value}</Typography>
        </Alert>
      </Box>
    );
  }

  // ── Primary flow ─────────────────────────────────────────────────────────────
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2">Service address</Typography>
        <Button
          size="small"
          onClick={() => setManualMode(true)}
          startIcon={<EditIcon sx={{ fontSize: 14 }} />}
          sx={{ fontSize: '0.75rem' }}
        >
          Enter manually
        </Button>
      </Box>

      {/* Step 1: Building search */}
      {!selectedBuilding && (
        <>
          {buildingsError ? (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {buildingsError} —{' '}
              <Button size="small" onClick={() => setManualMode(true)}>Enter manually</Button>
            </Alert>
          ) : (
            <Autocomplete
              key={autocompleteKey}
              options={buildings}
              loading={buildingsLoading}
              filterOptions={(options, state) => {
                const q = state.inputValue.toLowerCase().trim();
                if (!q) return options.slice(0, 100);
                const results = options.filter((b) => {
                  const haystack = [
                    b.buildingName,
                    b.address_subpremise,
                    b.address_street_number,
                    b.address_route,
                    `${b.address_street_number ?? ''} ${b.address_route ?? ''}`.trim(),
                    b.address_locality,
                    b.address_postal_code,
                  ].filter(Boolean).join(' ').toLowerCase();
                  return haystack.includes(q);
                });
                return results.slice(0, 30);
              }}
              getOptionLabel={buildingLabel}
              onChange={(_, b) => { if (b) handleBuildingSelect(b); }}
              noOptionsText={buildingsLoading ? 'Loading…' : 'No matching addresses — try a street name or suburb'}
              renderOption={(props, b) => (
                <li {...props} key={supaBuildingKey(b)}>
                  <Box sx={{ py: 0.25 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {b.buildingName.replace(/[\r\n]+/g, ' — ')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[b.address_street_number, b.address_route, b.address_locality, b.address_administrative_area_level_1, b.address_postal_code].filter(Boolean).join(' ')}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Search your building"
                  placeholder="Type a building name, street or suburb…"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {buildingsLoading && <CircularProgress size={16} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}
        </>
      )}

      {/* Building selected — show status */}
      {selectedBuilding && (
        <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Box>
            <Typography variant="body2" fontWeight={600}>{selectedBuilding.buildingName}</Typography>
            <Typography variant="caption" color="text.secondary">
              {[selectedBuilding.address_street_number, selectedBuilding.address_route, selectedBuilding.address_locality, selectedBuilding.address_administrative_area_level_1, selectedBuilding.address_postal_code].filter(Boolean).join(' ')}
            </Typography>
          </Box>
          <Button size="small" onClick={handleReset} sx={{ flexShrink: 0, fontSize: '0.75rem' }}>Change</Button>
        </Box>
      )}

      {/* Step 2: Loading SQ */}
      {sq.phase === 'loading' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">Checking availability…</Typography>
        </Box>
      )}

      {/* Step 2: Unit dropdown */}
      {sq.phase === 'units' && (
        <Autocomplete
          options={sq.units}
          getOptionLabel={unitLabel}
          onChange={(_, u) => { if (u) handleSelectUnit(u); }}
          renderOption={(props, u) => {
            const st = UNIT_STATUS[u.statusCode] ?? { label: u.statusCode, color: 'default' as const };
            return (
              <li {...props} key={u.rid}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                  <Typography variant="body2">{unitLabel(u)}</Typography>
                  <Chip label={st.label} color={st.color} size="small" />
                </Box>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label="Select your unit / apartment"
              placeholder="Type or scroll to find your unit…"
            />
          )}
          noOptionsText="No units found for this building"
        />
      )}

      {/* Not serviceable */}
      {sq.phase === 'not_serviceable' && (
        <Alert severity="error" icon={<CancelIcon fontSize="small" />} sx={{ mt: 0.5 }}
          action={<Button size="small" color="inherit" onClick={handleReset}>Search again</Button>}
        >
          {sq.description}
        </Alert>
      )}

      {/* Error */}
      {sq.phase === 'error' && (
        <Alert severity="warning" sx={{ mt: 0.5 }}
          action={<Button size="small" color="inherit" onClick={handleReset}>Try again</Button>}
        >
          {sq.message}
        </Alert>
      )}
    </Box>
  );
}
